import { useEffect, useRef, useState } from 'react';
import {
  FileText, StickyNote, Upload, Trash2, Save, Plus,
  Eye, EyeOff, Power, PowerOff, Loader2
} from 'lucide-react';
import { documentsAPI } from '../services/api.js';

// Working "reference documents as call context" manager (task #30).
//
// The admin can ADD NOTES (typed text) and UPLOAD DOCUMENTS that the AI uses on
// the relevant calls. Each row is scoped to an `area` (which pillar/section it
// belongs to) and carries two flags:
//   • active            — soft on/off
//   • injectIntoContext — whether it reaches the AI's call prompt
// The LiveKit agent reads active && injectIntoContext rows and injects their
// {title, content} into the system prompt alongside Knowledge/FAQs.
//
// Document text extraction happens CLIENT-SIDE here (plain text + a light PDF
// text pass), so the server stores the EXTRACTED TEXT the AI reads — no binary
// storage, no server-side parsing dependency. Files we can't extract from (e.g.
// a scanned PDF / .docx) are accepted as a titled placeholder the admin can
// paste real text into. The supported, fully-working path is .txt/.md/.csv and
// text-based .pdf; anything else falls back to "paste the text" gracefully.

const TEXT_EXT = ['txt', 'md', 'markdown', 'csv', 'json', 'log', 'text'];

function extOf(name) {
  const m = /\.([a-z0-9]+)$/i.exec(name || '');
  return m ? m[1].toLowerCase() : '';
}

// Best-effort text from a PDF without a heavy dependency: pull the text between
// BT/ET show-text operators. Works for text-based PDFs; returns '' for scanned
// (image-only) PDFs so the caller falls back to the paste path.
function extractPdfText(arrayBuffer) {
  try {
    const bytes = new Uint8Array(arrayBuffer);
    let raw = '';
    for (let i = 0; i < bytes.length; i++) raw += String.fromCharCode(bytes[i]);
    const chunks = [];
    // (string) Tj   and   [ (a) (b) ] TJ
    const re = /\(((?:\\.|[^()\\])*)\)\s*Tj|\[((?:[^\]])*)\]\s*TJ/g;
    let m;
    while ((m = re.exec(raw)) !== null) {
      if (m[1] != null) {
        chunks.push(m[1]);
      } else if (m[2] != null) {
        const inner = m[2].match(/\(((?:\\.|[^()\\])*)\)/g) || [];
        chunks.push(inner.map((s) => s.slice(1, -1)).join(''));
      }
    }
    return chunks
      .join(' ')
      .replace(/\\([()\\])/g, '$1')
      .replace(/\\n/g, '\n')
      .replace(/\s+\n/g, '\n')
      .trim();
  } catch {
    return '';
  }
}

async function readFileText(file) {
  const ext = extOf(file.name);
  if (TEXT_EXT.includes(ext) || (file.type || '').startsWith('text/')) {
    return (await file.text()).trim();
  }
  if (ext === 'pdf' || file.type === 'application/pdf') {
    const buf = await file.arrayBuffer();
    return extractPdfText(buf);
  }
  return ''; // unsupported binary — caller falls back to paste path
}

function fmtBytes(n) {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// `expertContextId` (optional) scopes this manager to one Expert-Mode context's
// attached documents — list is filtered to it and new docs are tagged with it.
export default function DocumentsManager({ area, title, blurb, expertContextId }) {
  const scope = expertContextId ? { expertContextId } : {};
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  // New-note composer.
  const [noteTitle, setNoteTitle] = useState('');
  const [noteBody, setNoteBody] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  // Upload state.
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [pasteFor, setPasteFor] = useState(null); // { fileName, mimeType, sizeBytes }
  const [pasteText, setPasteText] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await documentsAPI.list(area, expertContextId);
      setRows(r?.data || []);
    } catch (err) {
      setError(err.message || 'Failed to load documents.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [area, expertContextId]);

  const addNote = async () => {
    if (!noteTitle.trim() || !noteBody.trim()) return;
    setSavingNote(true);
    setError('');
    try {
      const r = await documentsAPI.create({
        area, kind: 'note', title: noteTitle.trim(), content: noteBody.trim(), ...scope
      });
      setRows((rs) => [r.data, ...rs]);
      setNoteTitle('');
      setNoteBody('');
    } catch (err) {
      setError(err.message || 'Failed to add note.');
    } finally {
      setSavingNote(false);
    }
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const text = await readFileText(file);
      const meta = { fileName: file.name, mimeType: file.type || null, sizeBytes: file.size };
      if (!text) {
        // Couldn't extract — let the admin paste the text for this file.
        setPasteFor(meta);
        setPasteText('');
        return;
      }
      const r = await documentsAPI.create({
        area, kind: 'document', title: file.name, content: text, ...meta, ...scope
      });
      setRows((rs) => [r.data, ...rs]);
    } catch (err) {
      setError(err.message || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const savePasted = async () => {
    if (!pasteFor || !pasteText.trim()) return;
    setUploading(true);
    setError('');
    try {
      const r = await documentsAPI.create({
        area, kind: 'document', title: pasteFor.fileName, content: pasteText.trim(), ...pasteFor, ...scope
      });
      setRows((rs) => [r.data, ...rs]);
      setPasteFor(null);
      setPasteText('');
    } catch (err) {
      setError(err.message || 'Save failed.');
    } finally {
      setUploading(false);
    }
  };

  const patch = async (row, body) => {
    setBusyId(row.id);
    try {
      const r = await documentsAPI.update(row.id, body);
      setRows((rs) => rs.map((x) => (x.id === row.id ? r.data : x)));
    } catch (err) {
      setError(err.message || 'Update failed.');
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.title}"? The AI will no longer use it.`)) return;
    setBusyId(row.id);
    try {
      await documentsAPI.remove(row.id);
      setRows((rs) => rs.filter((x) => x.id !== row.id));
    } catch (err) {
      setError(err.message || 'Delete failed.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className="card">
      <div className="mb-5">
        <h2 className="text-xl text-stone-100">{title || 'Documents & notes'}</h2>
        <p className="text-sm text-stone-400 mt-1">
          {blurb || 'Add notes and upload documents the AI uses on calls. Anything marked “used by AI” is injected into the call as reference material.'}
        </p>
      </div>

      {error && (
        <p className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-200">{error}</p>
      )}

      {/* ── Add a note ───────────────────────────────────────────────── */}
      <div className="rounded-xl border border-stone-800 p-4 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <StickyNote size={16} className="text-alfred-gold" />
          <h3 className="text-stone-100 font-medium text-sm">Add a note</h3>
        </div>
        <input
          className="field-input mb-2"
          placeholder="Note title — e.g. After-hours escalation policy"
          value={noteTitle}
          onChange={(e) => setNoteTitle(e.target.value)}
          maxLength={200}
        />
        <textarea
          className="field-input min-h-[90px] resize-y"
          placeholder="What should the AI know? e.g. If a caller asks about refunds, point them to billing@… and never promise a timeline."
          value={noteBody}
          onChange={(e) => setNoteBody(e.target.value)}
          maxLength={50000}
        />
        <div className="mt-2">
          <button
            type="button"
            className="btn-primary"
            onClick={addNote}
            disabled={savingNote || !noteTitle.trim() || !noteBody.trim()}
          >
            {savingNote ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
            {savingNote ? 'Adding…' : 'Add note'}
          </button>
        </div>
      </div>

      {/* ── Upload a document ────────────────────────────────────────── */}
      <div className="rounded-xl border border-stone-800 p-4 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Upload size={16} className="text-alfred-gold" />
          <h3 className="text-stone-100 font-medium text-sm">Upload a document</h3>
        </div>
        <p className="text-xs text-stone-500 mb-3">
          Text files (.txt, .md, .csv) and text-based PDFs are read automatically.
          For other files you'll be asked to paste the text.
        </p>
        <input
          ref={fileRef}
          type="file"
          className="hidden"
          accept=".txt,.md,.markdown,.csv,.json,.log,.pdf,text/*,application/pdf"
          onChange={onPickFile}
        />
        <button
          type="button"
          className="btn-ghost"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          {uploading ? 'Processing…' : 'Choose file'}
        </button>

        {pasteFor && (
          <div className="mt-3 rounded-lg border border-amber-500/40 bg-amber-500/5 p-3">
            <p className="text-xs text-amber-200 mb-2">
              Couldn't auto-read <strong>{pasteFor.fileName}</strong>. Paste the text the AI should use:
            </p>
            <textarea
              className="field-input min-h-[100px] resize-y"
              placeholder="Paste the document text here…"
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
            />
            <div className="mt-2 flex gap-2">
              <button type="button" className="btn-primary" onClick={savePasted} disabled={!pasteText.trim() || uploading}>
                <Save size={14} /> Save document
              </button>
              <button type="button" className="btn-ghost" onClick={() => { setPasteFor(null); setPasteText(''); }}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Existing rows ────────────────────────────────────────────── */}
      <h3 className="text-stone-300 text-sm font-medium mb-2">
        Saved items {rows.length > 0 && <span className="text-stone-500">({rows.length})</span>}
      </h3>
      {loading ? (
        <p className="text-sm text-stone-500">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-stone-500 italic">Nothing yet. Add a note or upload a document above.</p>
      ) : (
        <ul className="space-y-2">
          {rows.map((row) => (
            <li
              key={row.id}
              className={`rounded-lg border p-3 ${row.active && row.injectIntoContext ? 'border-stone-800' : 'border-stone-900 opacity-70'}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    {row.kind === 'document'
                      ? <FileText size={14} className="text-stone-400 shrink-0" />
                      : <StickyNote size={14} className="text-stone-400 shrink-0" />}
                    <span className="text-stone-100 text-sm truncate">{row.title}</span>
                  </div>
                  <p className="text-xs text-stone-500 mt-1 line-clamp-2">{row.content}</p>
                  <div className="text-[11px] text-stone-600 mt-1 flex flex-wrap gap-x-3">
                    <span className="capitalize">{row.kind}</span>
                    {row.sizeBytes != null && <span>{fmtBytes(row.sizeBytes)}</span>}
                    <span>{row.active ? 'active' : 'inactive'}</span>
                    <span>{row.injectIntoContext ? 'used by AI' : 'not used by AI'}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    title={row.injectIntoContext ? 'Stop using on calls' : 'Use on calls'}
                    className="p-1.5 rounded hover:bg-stone-800 text-stone-400 hover:text-alfred-gold"
                    onClick={() => patch(row, { injectIntoContext: !row.injectIntoContext })}
                    disabled={busyId === row.id}
                  >
                    {row.injectIntoContext ? <Eye size={15} /> : <EyeOff size={15} />}
                  </button>
                  <button
                    type="button"
                    title={row.active ? 'Disable' : 'Enable'}
                    className="p-1.5 rounded hover:bg-stone-800 text-stone-400 hover:text-alfred-gold"
                    onClick={() => patch(row, { active: !row.active })}
                    disabled={busyId === row.id}
                  >
                    {row.active ? <Power size={15} /> : <PowerOff size={15} />}
                  </button>
                  <button
                    type="button"
                    title="Delete"
                    className="p-1.5 rounded hover:bg-stone-800 text-red-300 hover:text-red-200"
                    onClick={() => remove(row)}
                    disabled={busyId === row.id}
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
